import { useEffect, useRef } from 'react';

/**
 * useDeferredMenuAction — the "dialog opens AFTER the menu closes" machinery
 * shared by `ViewControl` and `SavedViewsPane`'s `PaneRow` (clay-frontend-review
 * #6: this was duplicated byte-for-byte in both components).
 *
 * Glaze's `Dropdown` (floating-ui) closes on item click and its
 * `FloatingFocusManager` returns focus to the trigger in a microtask after
 * the menu unmounts. A name dialog / delete confirm opened straight from the
 * item's `onClick` mounts in the same commit, autofocuses its input, and
 * then has that focus yanked back to the trigger. So a dialog-opening item
 * only RECORDS its action (`afterClose`); `onOpenStateChange(false)` runs it
 * on the next macrotask, after the focus return has settled.
 *
 * `onOpen`, when supplied, runs on the `open === true` branch — `ViewControl`
 * passes `machine.refetchViews` (F11: refetch the saved-views list every
 * time the menu opens); a `PaneRow`'s own row menu has nothing to refetch
 * and passes nothing.
 */
export function useDeferredMenuAction(onOpen?: () => void): {
  afterClose: (action: () => void) => () => void;
  handleOpenStateChange: (open: boolean) => void;
} {
  const pendingActionRef = useRef<(() => void) | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  /** Item handler that defers `action` until the menu has fully closed (see module doc). */
  const afterClose = (action: () => void) => () => {
    pendingActionRef.current = action;
  };

  const handleOpenStateChange = (open: boolean) => {
    if (open) {
      pendingActionRef.current = null;
      onOpen?.();
      return;
    }
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) timerRef.current = window.setTimeout(action, 0);
  };

  return { afterClose, handleOpenStateChange };
}
